import { DatePipe } from '@angular/common';
import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer, Provider } from '@angular/core';

import { TooltipConfig } from 'ngx-bootstrap/tooltip';
import { provideEchartsCore } from 'ngx-echarts';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { firstValueFrom } from 'rxjs';

import { FormatBytePipe, FormatPercentPipe, NamingScaleFormatPipe } from '@bn/shared/core/pipes';
import { WorldGeoJsonService } from '@bn/shared/core/services';

/**
 * ngx-bootstrap tooltip defaults shared across the design system
 * (verbatim from the former WebUiModule providers).
 */
function provideTooltipDefaults(): Provider {
  return {
    provide: TooltipConfig,
    useFactory: (): TooltipConfig =>
      Object.assign(new TooltipConfig(), {
        adaptivePosition: false,
        placement: 'bottom',
        container: 'body',
      }),
  };
}

/**
 * Captured at boot (synchronously — no fetch) so `loadEcharts()` can reach the cached loader from
 * outside an injection context. `provideEchartsCore` takes a plain function, not a factory.
 */
let worldGeoJson: WorldGeoJsonService | null = null;
let worldMapWanted = false;
let worldMapRegistered = false;

/**
 * Loads the chart bundle on first use and, for apps that render maps, registers the `world` map.
 *
 * Both are off the boot path on purpose: echarts/core plus every registered chart type is ~780 kB,
 * and the country geometry is a large fetch. ngx-echarts awaits this before the first chart
 * renders, so nothing draws against an unregistered map.
 */
async function loadEcharts(): Promise<(typeof import('./chart/echarts.setup'))['echarts']> {
  const { echarts } = await import('./chart/echarts.setup');

  if (worldMapWanted && !worldMapRegistered && worldGeoJson) {
    const collection = await firstValueFrom(worldGeoJson.load());
    echarts.registerMap('world', collection as Parameters<typeof echarts.registerMap>[1]);
    worldMapRegistered = true;
  }

  return echarts;
}

/**
 * Root providers and global side effects for the `@bn/web-ui` design system —
 * the new home of everything that used to live in `WebUiModule`'s `providers`
 * block. Register once in the application config (see `apps/dashboard`'s
 * `app.config.ts`). The chart bundle (echarts/core plus every registered chart type, ~780 kB)
 * is loaded on the first chart render, not at boot — see `loadEcharts()`.
 *
 * @param options.worldMap register the ECharts `world` map when the chart bundle loads
 * (default `true`). Apps without map charts (e.g. partner-portal) pass `false` and never fetch
 * the country geometry.
 */
export function provideWebUi(options: { worldMap?: boolean } = {}): EnvironmentProviders {
  const { worldMap = true } = options;
  worldMapWanted = worldMap;

  return makeEnvironmentProviders([
    // Synchronous: it only grabs the (lazy) loader so `loadEcharts()` can use it later.
    provideAppInitializer(() => {
      worldGeoJson = inject(WorldGeoJsonService);
    }),
    provideTooltipDefaults(),
    provideEchartsCore({ echarts: loadEcharts }),
    provideNgxSkeletonLoader({ animation: 'progress' }),
    FormatBytePipe,
    FormatPercentPipe,
    NamingScaleFormatPipe,
    DatePipe,
  ]);
}
